'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useCallback } from 'react';
import { HealthResponse } from '@/lib/types';
import { aiModelsApi, HealthResponse as AiHealthResponse } from '@/lib/ai-models-api';

interface MinimalStats {
  processing: number;
  pending: number;
  completed: number;
}

interface StatusBarProps {
  health: HealthResponse | null;
  stats: MinimalStats | null;
  onRefresh: () => void;
}

const navItems = [
  { href: '/', label: 'Create', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )},
  { href: '/variants', label: 'Variants', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  )},
  { href: '/collage', label: 'Collage', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  )},
  { href: '/img2img', label: 'Img2Img', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
    </svg>
  )},
  { href: '/videos', label: 'Videos', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
    </svg>
  )},
  { href: '/ltx', label: 'LTX', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-2.625 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0118 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 016 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 12 6 12.504 6 13.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m0-3.75h-1.5" />
    </svg>
  )},
  { href: '/pose', label: 'Pose', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  )},
  { href: '/ai-models', label: 'AI Models', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  )},
  { href: '/favorites', label: 'Favorites', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  )},
];

export default function StatusBar({ health, stats, onRefresh }: StatusBarProps) {
  const pathname = usePathname();
  const [aiHealth, setAiHealth] = useState<AiHealthResponse | null>(null);
  const [aiHealthLoading, setAiHealthLoading] = useState(true);
  const [showAiPopup, setShowAiPopup] = useState(false);

  const fetchAiHealth = useCallback(async () => {
    try {
      const res = await aiModelsApi.getHealth();
      setAiHealth(res);
    } catch {
      setAiHealth(null);
    } finally {
      setAiHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAiHealth();
    const interval = setInterval(fetchAiHealth, 10000);
    return () => clearInterval(interval);
  }, [fetchAiHealth]);

  const aiConnected = aiHealth !== null;

  return (
    <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
      <div className="max-w-7xl mx-auto px-6 h-12 flex items-center justify-between">
        {/* Left: Navigation */}
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
                  isActive
                    ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Right: Status & Actions */}
        <div className="flex items-center gap-4">
          {/* Queue Stats */}
          {stats && (
            <div className="flex items-center gap-3 text-xs">
              {stats.processing > 0 && (
                <span className="text-[var(--info)] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[var(--info)] animate-pulse-soft" />
                  {stats.processing} active
                </span>
              )}
              {stats.pending > 0 && (
                <span className="text-[var(--warning)]">
                  {stats.pending} queued
                </span>
              )}
              <span className="text-[var(--text-muted)]">
                {stats.completed} generated
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="w-px h-4 bg-[var(--border-subtle)]" />

          {/* ComfyUI Status */}
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${
              health?.comfyui_connected
                ? 'bg-[var(--success)]'
                : 'bg-[var(--text-muted)]'
            }`} />
            <span className="text-xs text-[var(--text-muted)] font-medium">
              ComfyUI
            </span>
          </div>

          {/* AI Models Status with Hover Popup */}
          <div
            className="relative"
            onMouseEnter={() => setShowAiPopup(true)}
            onMouseLeave={() => setShowAiPopup(false)}
          >
            <div className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-[var(--bg-hover)] transition-colors">
              <div className={`w-1.5 h-1.5 rounded-full ${
                aiHealthLoading
                  ? 'bg-[var(--text-muted)] animate-pulse'
                  : aiConnected
                    ? 'bg-[var(--success)]'
                    : 'bg-[var(--error)]'
              }`} />
              <span className="text-xs text-[var(--text-muted)] font-medium">
                AI Models
              </span>
            </div>

            {/* Hover Popup */}
            {showAiPopup && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-xl shadow-xl p-4 z-50">
                <h3 className="text-sm font-semibold mb-3">AI Models Service</h3>

                {aiHealthLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                    <div className="w-3 h-3 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
                    Connecting...
                  </div>
                ) : !aiConnected ? (
                  <div className="flex items-center gap-2 text-sm text-[var(--error)]">
                    <div className="w-2 h-2 rounded-full bg-[var(--error)]" />
                    Service unavailable
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Status</p>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          <span className="font-medium text-green-400">Connected</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Device</p>
                        <p className="font-medium">{aiHealth?.device || 'N/A'}</p>
                      </div>
                      {aiHealth?.gpu_available && (
                        <>
                          <div className="col-span-2">
                            <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">GPU</p>
                            <p className="font-medium truncate" title={aiHealth.gpu_name}>
                              {aiHealth.gpu_name || 'Available'}
                            </p>
                          </div>
                          {aiHealth.gpu_memory_total && (
                            <div>
                              <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">VRAM</p>
                              <p className="font-medium">
                                {aiHealth.gpu_memory_free ? (aiHealth.gpu_memory_free / 1024).toFixed(1) : '?'} / {(aiHealth.gpu_memory_total / 1024).toFixed(1)} GB
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {aiHealth?.queue && (
                        <div>
                          <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Queue</p>
                          <p className="font-medium">{aiHealth.queue.pending} pending</p>
                        </div>
                      )}
                    </div>

                    {aiHealth?.loaded_models && aiHealth.loaded_models.length > 0 && (
                      <div className="pt-2 border-t border-[var(--border-subtle)]">
                        <p className="text-[var(--text-muted)] uppercase tracking-wider text-xs mb-1.5">Loaded Models</p>
                        <div className="flex flex-wrap gap-1">
                          {aiHealth.loaded_models.map((model) => (
                            <span
                              key={model}
                              className="px-1.5 py-0.5 bg-[var(--bg-primary)] rounded text-xs font-mono"
                            >
                              {model}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Refresh */}
          <button
            onClick={onRefresh}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-subtle rounded-lg hover:bg-[var(--bg-hover)] cursor-pointer"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
